import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Autocomplete from "react-google-autocomplete";
import cx from "clsx";
import { Modal } from "../../shared/Modal/Modal";
import { useCurrentAccountQuery } from "../../api/queries/useAccountQuery";
import { InputLabel } from "../../shared/InputLabel/InputLabel";
import { Select } from "../../shared/Select/Select";
import { Input } from "../../shared/Input/Input";
import styles from "./StripeMailingAddressModal.module.scss";
import { Button } from "../../shared/Button/Button";
import { useStripeTaxAddress } from "./hooks/useStripeTaxAddress";
import { CircularProgress } from "../../shared/CircularProgress/CircularProgress";
import { useSetStripeTaxAddress } from "./hooks/useSetStripeTaxAddress";
import { uiNotification } from "../../services/UINotificationService";
import { useCurrentUserQuery } from "../../api/queries/useUserQuery";
import { Checkbox } from "../../shared/Checkbox/Checkbox";

const locationSchema = yup.object({
  isSelected: yup.boolean(),
  stripeAccountId: yup.string().required(),
  country: yup.object().when("isSelected", {
    is: true,
    then: (s) =>
      s.shape({
        label: yup.string().required(),
        value: yup.string().required(),
      }),
  }),
  streetAddress: yup.string().when("isSelected", {
    is: true,
    then: (s) => s.required(),
  }),
  city: yup.string().when("isSelected", {
    is: true,
    then: (s) => s.required(),
  }),
  state: yup.string().when("isSelected", {
    is: true,
    then: (s) => s.required(),
  }),
  zipCode: yup.string().when("isSelected", {
    is: true,
    then: (s) => s.required(),
  }),
});

const addressSchema = yup.object({
  locations: yup.array().of(locationSchema),
});
const StripeMailingAddressModal = () => {
  const { data: account } = useCurrentAccountQuery();
  const { data: user } = useCurrentUserQuery();

  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading } = useStripeTaxAddress({
    enabled: isOpen,
  });
  const { mutate, isLoading: isMutating } = useSetStripeTaxAddress();

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      locations: [
        {
          stripeAccountId: "",
          clinicName: "",
          isSelected: false,
          country: { label: "", value: "" },
          streetAddress: "",
          suite: "",
          city: "",
          state: "",
          zipCode: "",
        },
      ],
    },

    validationSchema: addressSchema,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => {
      if (values.locations.every((loc) => !loc.isSelected)) {
        uiNotification.error("Please select at least one location");
        return;
      }
      const payload = {
        locations: values.locations
          .filter((loc) => loc.isSelected)
          .map((loc) => ({
            stripe_account_id: loc.stripeAccountId,
            line1: loc.streetAddress,
            line2: loc.suite || undefined,
            city: loc.city,
            state: loc.state,
            postal_code: loc.zipCode,
            country: loc.country.value,
          })),
      };

      mutate(payload, {
        onSuccess: () => {
          uiNotification.success("Addresses updated successfully");
          setIsOpen(false);
        },
        onError: () => {
          uiNotification.error("Failed to update addresses");
        },
      });
    },
  });

  const parseGooglePlace = (place) => {
    let address = "";
    let city = "";
    let state = "";
    let country = "";
    let zipcode = "";

    if (!place?.address_components) {
      return { address, city, state, country, zipcode };
    }

    let streetNumber = "";
    let route = "";

    place.address_components.forEach((component) => {
      const { types, long_name, short_name } = component;

      if (types.includes("street_number")) {
        streetNumber = long_name;
      }

      if (types.includes("route")) {
        route = long_name;
      }

      if (types.includes("locality") || types.includes("postal_town")) {
        city = long_name;
      }

      if (!city && types.includes("administrative_area_level_2")) {
        city = long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        state = short_name;
      }

      if (types.includes("country")) {
        country = short_name;
      }

      if (types.includes("postal_code")) {
        zipcode = long_name;
      }

      if (!zipcode && types.includes("postal_code_prefix")) {
        zipcode = long_name;
      }
    });

    address = [streetNumber, route].filter(Boolean).join(" ");

    return {
      address,
      city,
      state,
      country,
      zipcode,
    };
  };

  useEffect(() => {
    if (user?.isMainAdmin) {
      setIsOpen(account?.stripeTaxAddressPrompt?.should_show);
    }
  }, [account, user]);

  useEffect(() => {
    if (!data?.locations || !countryOptions) return;

    const singleLocation = data.locations.length === 1;

    const mappedLocations = data.locations.map((loc) => {
      const address = loc.address;

      const countryOption = countryOptions.find(
        (c) => c.value === address?.country,
      );

      return {
        isSelected: singleLocation ? true : false,
        stripeAccountId: loc.stripe_account_id,
        clinicName: loc.clinic_name,
        country: countryOption || { label: "", value: "" },
        streetAddress: address?.line1 || "",
        suite: address?.line2 || "",
        city: address?.city || "",
        state: address?.state || "",
        zipCode: address?.postal_code || "",
      };
    });

    setFieldValue("locations", mappedLocations);
  }, [data, countryOptions]);

  const countryOptions = useMemo(() => {
    return account?.countryList?.map((country) => ({
      label: country.country_name,
      value: country.country_code,
    }));
  }, [account]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      header={<Modal.Title>Review & Confirm Your Address</Modal.Title>}
      footer={
        <Button
          size="small"
          onClick={handleSubmit}
          isDisabled={isLoading || isMutating}
          leftAdornment={
            isMutating ? <CircularProgress color="white" size="tiny" /> : null
          }
        >
          Confirm
        </Button>
      }
      footerClassName={styles.modalFooter}
      shouldCloseOnOverlayClick={false}
    >
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div className={styles.container}>
          <div>
            <p className="bold">
              We are preparing to send your 2025 tax documents.
            </p>
            <p>
              Please confirm or edit your mailing address on file with Stripe to
              ensure you receive a paper copy.
            </p>
          </div>
          {values.locations.map((location, index) => {
            const isDisabled = !location.isSelected;

            return (
              <div
                key={location.stripeAccountId}
                className={styles.clinicContainer}
              >
                {values.locations.length > 1 && (
                  <div>
                    <Checkbox
                      isChecked={location.isSelected}
                      onChange={(e) =>
                        setFieldValue(
                          `locations.${index}.isSelected`,
                          e.target.checked,
                        )
                      }
                      label={<span>{location.clinicName}</span>}
                    />
                  </div>
                )}

                <div className={styles.countrySelect}>
                  <InputLabel className={styles.label}>
                    Country <span className="required">*</span>
                  </InputLabel>
                  <Select
                    size={"small"}
                    options={countryOptions}
                    value={location.country}
                    onChange={(value) =>
                      setFieldValue(`locations.${index}.country`, value)
                    }
                    isSearchable
                    isError={
                      location.isSelected &&
                      !!errors?.locations?.[index]?.country
                    }
                    isDisabled={isDisabled}
                  />
                </div>

                <div className={styles.grid2Cols}>
                  <div className={styles.autocompleteContainer}>
                    <InputLabel className={styles.label}>
                      Street Address <span className="required">*</span>
                    </InputLabel>
                    <Autocomplete
                      value={location.streetAddress}
                      placeholder="Search Address"
                      className={cx(styles.autocompleteInput, {
                        [styles.error]:
                          location.isSelected &&
                          !!errors?.locations?.[index]?.streetAddress,
                      })}
                      onChange={(e) =>
                        setFieldValue(
                          `locations.${index}.streetAddress`,
                          e.target.value,
                        )
                      }
                      onPlaceSelected={(place) => {
                        const parsed = parseGooglePlace(place);
                        setFieldValue(
                          `locations.${index}.streetAddress`,
                          parsed.address,
                        );
                        setFieldValue(`locations.${index}.suite`, "");
                        setFieldValue(`locations.${index}.city`, parsed.city);
                        setFieldValue(`locations.${index}.state`, parsed.state);
                        setFieldValue(
                          `locations.${index}.zipCode`,
                          parsed.zipcode,
                        );

                        const selectedCountry = countryOptions.find(
                          (c) => c.value === parsed.country,
                        );

                        if (selectedCountry) {
                          setFieldValue(
                            `locations.${index}.country`,
                            selectedCountry,
                          );
                        }
                      }}
                      types={[]}
                      fields={[
                        "address_component",
                        "formatted_address",
                        "geometry",
                        "name",
                        "place_id",
                      ]}
                      disabled={isDisabled}
                    />
                  </div>
                  <div>
                    <InputLabel className={styles.label}>
                      Unit/Suite Number
                    </InputLabel>
                    <Input
                      size={"small"}
                      value={location.suite}
                      onChange={(e) =>
                        setFieldValue(
                          `locations.${index}.suite`,
                          e.target.value,
                        )
                      }
                      isDisabled={isDisabled}
                    />
                  </div>
                </div>

                <div className={styles.grid3Cols}>
                  <div>
                    <InputLabel className={styles.label}>
                      City <span className="required">*</span>
                    </InputLabel>
                    <Input
                      size={"small"}
                      value={location.city}
                      onChange={(e) =>
                        setFieldValue(`locations.${index}.city`, e.target.value)
                      }
                      isError={
                        location.isSelected &&
                        !!errors?.locations?.[index]?.city
                      }
                      isDisabled={isDisabled}
                    />
                  </div>
                  <div>
                    <InputLabel className={styles.label}>
                      State <span className="required">*</span>
                    </InputLabel>
                    <Input
                      size={"small"}
                      value={location.state}
                      onChange={(e) =>
                        setFieldValue(
                          `locations.${index}.state`,
                          e.target.value,
                        )
                      }
                      isError={
                        location.isSelected &&
                        !!errors?.locations?.[index]?.state
                      }
                      isDisabled={isDisabled}
                    />
                  </div>
                  <div>
                    <InputLabel className={styles.label}>
                      Zip Code <span className="required">*</span>
                    </InputLabel>
                    <Input
                      size={"small"}
                      value={location.zipCode}
                      onChange={(e) =>
                        setFieldValue(
                          `locations.${index}.zipCode`,
                          e.target.value,
                        )
                      }
                      isError={
                        location.isSelected &&
                        !!errors?.locations?.[index]?.zipCode
                      }
                      isDisabled={isDisabled}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default StripeMailingAddressModal;
